import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send, Mail, Phone } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";

const getSessionId = () => {
  let id = localStorage.getItem("sony_chat_session");
  if (!id) {
    id = "chat-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("sony_chat_session", id);
  }
  return id;
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hello — welcome to Shop ONline New York. Ask me about our pieces, delivery across NYC, member pricing or returns." },
  ]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [escalate, setEscalate] = useState(null); // null | "choose" | "email" | "phone"
  const [contact, setContact] = useState("");
  const [details, setDetails] = useState("");
  const [escalated, setEscalated] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, thinking, escalate]);

  const send = async () => {
    const text = input.trim();
    if (!text || thinking) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setThinking(true);
    setMessages((m) => [...m, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API_BASE}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: getSessionId(), message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", text: acc };
          return copy;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "Sorry — I lost my footing. Please try again." };
        return copy;
      });
    } finally {
      setThinking(false);
    }
  };

  const submitEscalation = async () => {
    try {
      await api.post("/chat/escalate", {
        session_id: getSessionId(), preference: escalate, contact, details,
      });
      setEscalated(true);
      setMessages((m) => [...m, {
        role: "assistant",
        text: `Thank you — that's noted. Our team will reach out within the next 48 hours by ${escalate === "email" ? "email" : "phone"}. Anything else I can help with meanwhile?`,
      }]);
      setEscalate(null);
      setContact("");
      setDetails("");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const inputCls = "w-full rounded-md border border-charcoal/20 bg-transparent px-3 py-2.5 text-sm outline-none placeholder:text-ash/60 focus:border-charcoal";

  return (
    <>
      <button
        data-testid="chat-open-button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Chat with us"
        className="fixed bottom-6 right-6 z-[90] flex h-14 w-14 items-center justify-center rounded-full bg-charcoal text-bone shadow-lg transition-colors duration-300 hover:bg-moss"
      >
        {open ? <X size={20} /> : <MessageCircle size={20} />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-6 z-[90] flex h-[520px] w-[380px] max-w-[calc(100vw-3rem)] flex-col overflow-hidden rounded-lg border border-charcoal/10 bg-bone shadow-2xl"
          >
            <div className="bg-charcoal px-5 py-4 text-bone">
              <p className="font-serif text-lg">Shop assistant</p>
              <p className="text-xs font-light text-bone/60">Answers instantly · team replies within 48 hours</p>
            </div>
            <div className="flex-1 space-y-4 overflow-y-auto px-5 py-5">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <p className={`max-w-[80%] rounded-lg px-4 py-2.5 text-sm font-light leading-relaxed ${
                    m.role === "user" ? "bg-charcoal text-bone" : "bg-sand text-charcoal"}`}>
                    {m.text}{thinking && i === messages.length - 1 && m.role === "assistant" && <span className="animate-pulse">▍</span>}
                  </p>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
            {!escalated && !escalate && (
              <button data-testid="chat-escalate-button" onClick={() => setEscalate("choose")}
                className="mx-5 mb-2 text-left text-[11px] uppercase tracking-[0.15em] text-taupe transition-colors duration-300 hover:text-charcoal">
                Need a person? Talk to the team
              </button>
            )}
            {escalate === "choose" && (
              <div className="mx-5 mb-3 rounded-md border border-charcoal/10 bg-sand/60 p-4" data-testid="escalate-choose">
                <p className="mb-3 text-xs font-light text-ash">How should our team reach you? We'll respond within 48 hours.</p>
                <div className="flex gap-2">
                  <button data-testid="escalate-email" onClick={() => setEscalate("email")}
                    className="flex flex-1 items-center justify-center gap-2 rounded-full border border-charcoal/20 py-2 text-xs uppercase tracking-[0.15em] hover:bg-charcoal hover:text-bone">
                    <Mail size={13} /> Email
                  </button>
                  <button data-testid="escalate-phone" onClick={() => setEscalate("phone")}
                    className="flex flex-1 items-center justify-center gap-2 rounded-full border border-charcoal/20 py-2 text-xs uppercase tracking-[0.15em] hover:bg-charcoal hover:text-bone">
                    <Phone size={13} /> Phone
                  </button>
                </div>
              </div>
            )}
            {(escalate === "email" || escalate === "phone") && (
              <div className="mx-5 mb-3 space-y-2 rounded-md border border-charcoal/10 bg-sand/60 p-4" data-testid="escalate-form">
                <input data-testid="escalate-contact"
                  placeholder={escalate === "email" ? "Your email address" : "Your phone number"}
                  value={contact} onChange={(e) => setContact(e.target.value)} className={inputCls} />
                <textarea data-testid="escalate-details" rows={2} placeholder="What should we help with? (optional)"
                  value={details} onChange={(e) => setDetails(e.target.value)} className={inputCls} />
                <div className="flex gap-2">
                  <button data-testid="escalate-submit" onClick={submitEscalation} disabled={!contact}
                    className="flex-1 rounded-full bg-charcoal py-2.5 text-xs uppercase tracking-[0.15em] text-bone hover:bg-moss disabled:opacity-60">
                    Request callback
                  </button>
                  <button onClick={() => setEscalate("choose")} className="px-3 text-xs text-ash hover:text-charcoal">Back</button>
                </div>
              </div>
            )}
            <div className="flex items-center gap-2 border-t border-charcoal/10 p-4">
              <input
                data-testid="chat-input"
                placeholder="Ask about pieces, delivery, returns…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                className={inputCls}
              />
              <button data-testid="chat-send" onClick={send} disabled={thinking || !input.trim()}
                className="rounded-full bg-charcoal p-3 text-bone transition-colors duration-300 hover:bg-moss disabled:opacity-50"
                aria-label="Send message">
                <Send size={15} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
